import Button from '@mui/material/Button';
import { DatePicker, DatePickerSlotProps } from '@mui/x-date-pickers';
import { PickersDayProps } from '@mui/x-date-pickers/PickersDay/PickersDay';
import { format } from 'date-fns/format';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import * as React from 'react';
import { DateFormatDateFns } from '../../utils/dateFormat';
import { DayIconButton, DayWrapper, StyledRange } from './styled';

interface DateRangeProps {
  fromDate: Date;
  toDate: Date;
  onApply: (fromDate: Date, toDate: Date) => void;
}

type DayViewProps = Pick<
  PickersDayProps<Date>,
  'day' | 'disabled' | 'onDaySelect' | 'outsideCurrentMonth' | 'selected'
> & {
  fromDate: Date;
  toDate: Date;
};

const DayView: React.FC<DayViewProps> = ({
  day: date,
  disabled,
  fromDate,
  onDaySelect,
  outsideCurrentMonth,
  selected,
  toDate,
}) => (
  <DayWrapper
    isDayInCurrentMonth={!outsideCurrentMonth}
    isDayBetween={isAfter(date, fromDate) && isBefore(date, toDate)}
    isFirstDay={isSameDay(date, fromDate)}
    isLastDay={isSameDay(date, toDate)}
  >
    <DayIconButton disabled={disabled} isSelected={!!selected} onClick={() => onDaySelect(date)}>
      <span>{format(date, DateFormatDateFns.Day)}</span>
    </DayIconButton>
  </DayWrapper>
);

const textFieldProps: DatePickerSlotProps<Date, false> = { textField: { size: 'small', variant: 'outlined' } };

export const DateRangePicker: React.FC<DateRangeProps> = ({
  fromDate: appliedFromDate,
  toDate: appliedToDate,
  onApply,
}) => {
  const [fromDate, setFromDate] = React.useState<Date>(appliedFromDate);
  const [toDate, setToDate] = React.useState<Date>(appliedToDate);

  return (
    <StyledRange>
      <DatePicker
        format={DateFormatDateFns.Short}
        label="From"
        onChange={(date) => {
          if (!date) {
            return;
          }
          setFromDate(date);
        }}
        slotProps={textFieldProps}
        slots={{
          day: ({ day, onDaySelect, outsideCurrentMonth, selected }) => (
            <DayView
              day={day}
              disabled={isAfter(day, toDate)}
              fromDate={fromDate}
              onDaySelect={onDaySelect}
              outsideCurrentMonth={outsideCurrentMonth}
              selected={selected}
              toDate={toDate}
            />
          ),
        }}
        value={fromDate}
      />
      <DatePicker
        format={DateFormatDateFns.Short}
        label="To"
        onChange={(date) => {
          if (!date) {
            return;
          }
          setToDate(date);
        }}
        slotProps={textFieldProps}
        slots={{
          day: ({ day, onDaySelect, outsideCurrentMonth, selected }) => (
            <DayView
              day={day}
              disabled={isBefore(day, fromDate)}
              fromDate={fromDate}
              onDaySelect={onDaySelect}
              outsideCurrentMonth={outsideCurrentMonth}
              selected={selected}
              toDate={toDate}
            />
          ),
        }}
        value={toDate}
      />
      <Button color="primary" onClick={() => onApply(fromDate, toDate)} variant="contained">
        Search
      </Button>
    </StyledRange>
  );
};
